/**
 * SEO utility functions for managing meta tags
 */

/**
 * Update or create a meta description tag in the document head
 * @param description - The meta description text (150-160 characters recommended)
 */
export function setMetaDescription(description: string): void {
  // Remove existing meta description if it exists
  let metaDescription = document.querySelector('meta[name="description"]');
  
  if (!metaDescription) {
    // Create new meta description tag
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  
  // Update the content
  metaDescription.setAttribute('content', description);
}

/**
 * Remove meta description tag (useful for cleanup)
 */
export function removeMetaDescription(): void {
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.remove();
  }
}

/**
 * Get the canonical base URL (www. version, no trailing slash)
 * Works in both server and client contexts
 */
function getCanonicalBaseUrl(): string {
  // In browser, use window.location
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    // Normalize to www. version if needed (for production)
    if (origin.includes("munthlyapp.com") && !origin.includes("www.")) {
      return origin.replace("munthlyapp.com", "www.munthlyapp.com");
    }
    return origin;
  }
  // In server context, use environment variable or default
  // Check for process safely
  if (typeof process !== "undefined" && process.env && process.env.APP_URL) {
    const url = process.env.APP_URL;
    // Ensure www. version and no trailing slash
    let normalized = url.replace(/\/$/, "");
    if (!normalized.includes("www.") && normalized.includes("munthlyapp.com")) {
      normalized = normalized.replace(/^(https?:\/\/)(munthlyapp\.com)/, "$1www.$2");
    }
    return normalized;
  }
  // Fallback for server-side rendering
  return "https://www.munthlyapp.com";
}

/**
 * Set or update the canonical URL for the page
 * @param path - The path (e.g., "/about" or "/dashboard") - should not include trailing slash
 * @returns Cleanup function to remove the canonical link
 */
export function setCanonicalUrl(path: string): () => void {
  if (typeof window === "undefined") {
    return () => {}; // No-op in SSR
  }

  // Remove trailing slash from path
  const cleanPath = path.replace(/\/$/, "") || "/";
  
  // Get base URL and ensure www. version
  const baseUrl = getCanonicalBaseUrl();
  const canonicalUrl = `${baseUrl}${cleanPath}`;

  // Remove existing canonical link if it exists
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!canonicalLink) {
    // Create new canonical link tag
    canonicalLink = document.createElement("link");
    canonicalLink.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalLink);
  }
  
  // Update the href
  canonicalLink.setAttribute("href", canonicalUrl);

  // Return cleanup function
  return () => {
    if (canonicalLink && canonicalLink.parentNode) {
      canonicalLink.parentNode.removeChild(canonicalLink);
    }
  };
}

/**
 * Remove canonical URL tag (useful for cleanup)
 */
export function removeCanonicalUrl(): void {
  if (typeof window === "undefined") return;
  
  const canonicalLink = document.querySelector('link[rel="canonical"]');
  if (canonicalLink) {
    canonicalLink.remove();
  }
}

/**
 * Set or update Open Graph meta tags for social media sharing
 * @param title - The page title
 * @param description - The page description
 * @param url - The page URL (path, will be combined with base URL)
 * @param image - Optional image URL (defaults to og-image.png)
 * @param type - Optional Open Graph type (defaults to "website")
 * @returns Cleanup function to remove Open Graph tags
 */
export function setOpenGraphTags(
  title: string,
  description: string,
  url: string,
  image?: string,
  type: string = "website"
): () => void {
  if (typeof window === "undefined") {
    return () => {}; // No-op in SSR
  }

  const baseUrl = getCanonicalBaseUrl();
  const cleanPath = url.replace(/\/$/, "") || "/";
  const fullUrl = `${baseUrl}${cleanPath}`;
  const imageUrl = image || `${baseUrl}/og-image.png`;

  // Helper to set or update meta property
  const setMetaProperty = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", property);
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
  };

  setMetaProperty("og:title", title);
  setMetaProperty("og:description", description);
  setMetaProperty("og:url", fullUrl);
  setMetaProperty("og:type", type);
  setMetaProperty("og:image", imageUrl);
  setMetaProperty("og:site_name", "Munthly");

  // Return cleanup function
  return () => {
    const ogTags = document.querySelectorAll('meta[property^="og:"]');
    ogTags.forEach(tag => tag.remove());
  };
}

/**
 * Remove Open Graph tags (useful for cleanup)
 */
export function removeOpenGraphTags(): void {
  if (typeof window === "undefined") return;
  const ogTags = document.querySelectorAll('meta[property^="og:"]');
  ogTags.forEach(tag => tag.remove());
}

/**
 * Set or update Twitter Card meta tags
 * @param title - The page title
 * @param description - The page description
 * @param image - Optional image URL (defaults to og-image.png)
 * @param cardType - Twitter card type (defaults to "summary_large_image")
 * @returns Cleanup function to remove Twitter Card tags
 */
export function setTwitterCardTags(
  title: string,
  description: string,
  image?: string,
  cardType: string = "summary_large_image"
): () => void {
  if (typeof window === "undefined") {
    return () => {}; // No-op in SSR
  }

  const baseUrl = getCanonicalBaseUrl();
  const imageUrl = image || `${baseUrl}/og-image.png`;

  // Helper to set or update meta name
  const setMetaName = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", name);
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
  };

  setMetaName("twitter:card", cardType);
  setMetaName("twitter:title", title);
  setMetaName("twitter:description", description);
  setMetaName("twitter:image", imageUrl);

  // Return cleanup function
  return () => {
    const twitterTags = document.querySelectorAll('meta[name^="twitter:"]');
    twitterTags.forEach(tag => tag.remove());
  };
}

/**
 * Remove Twitter Card tags (useful for cleanup)
 */
export function removeTwitterCardTags(): void {
  if (typeof window === "undefined") return;
  const twitterTags = document.querySelectorAll('meta[name^="twitter:"]');
  twitterTags.forEach(tag => tag.remove());
}

/**
 * Set or update meta robots tag
 * @param content - Robots directive (e.g., "noindex, nofollow" or "index, follow")
 * @returns Cleanup function to remove meta robots tag
 */
export function setMetaRobots(content: string): () => void {
  if (typeof window === "undefined") {
    return () => {}; // No-op in SSR
  }

  let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
  if (!metaRobots) {
    metaRobots = document.createElement("meta");
    metaRobots.setAttribute("name", "robots");
    document.head.appendChild(metaRobots);
  }
  metaRobots.setAttribute("content", content);

  // Return cleanup function
  return () => {
    if (metaRobots && metaRobots.parentNode) {
      metaRobots.parentNode.removeChild(metaRobots);
    }
  };
}

/**
 * Remove meta robots tag (useful for cleanup)
 */
export function removeMetaRobots(): void {
  if (typeof window === "undefined") return;
  const metaRobots = document.querySelector('meta[name="robots"]');
  if (metaRobots) {
    metaRobots.remove();
  }
}

